// DeleteAccountModal.tsx
import axios from "axios";
import React, { useState } from "react";
import { env, useGlobalState } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import { clearChatHistory } from "../chatbot/chatHistoryStorage";
import { useNavigate } from "react-router-dom";
import { getToken, clearToken } from "../../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { List, Stack, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";

const DeleteAccountModal: React.FC = () => {
  const {
    isDeleteAccountModalVisible,
    closeDeleteAccountModal,
    showErrorMessage,
  } = useModalState();

  const navigate = useNavigate();
  const { refreshAccountInfo, setBearerToken } = useGlobalState();

  const [isLoading, setIsLoading] = useState(false);

  const doDeleteAccount = async () => {
    if (process.env.NODE_ENV === 'development') console.log("Deleting account...");
    const bearerToken = getToken();
    if (!bearerToken) {
      showErrorMessage("No bearer token found. Please sign in first.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/delete_account`,
        {},
        { headers: { Authorization: `Bearer ${bearerToken}` } }
      );
      if (process.env.NODE_ENV === 'development') console.log("Account deleted successfully:", response.data);
      clearToken();
      setBearerToken(null)
      clearChatHistory();
      refreshAccountInfo();
      closeDeleteAccountModal();
      navigate("/dashboard");
    } catch (error: any) {
      showErrorMessage(
        `An unexpected error occurred ${error?.response?.data?.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot lazyMount open={isDeleteAccountModalVisible} onOpenChange={(e) => { if (!e.open) closeDeleteAccountModal(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle color="#dc2626">Delete account?</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Text fontSize="md" color="#52525b" lineHeight="24px">
              Deleting your account will cause the following:
            </Text>
            <List.Root paddingLeft={5} color="#52525b" fontSize="md" lineHeight="28px">
              <List.Item>You will permanently lose all accrued CoinCap points.</List.Item>
              <List.Item>You will permanently lose all chat history.</List.Item>
              <List.Item>All API keys and API history will be deleted.</List.Item>
              <List.Item>Your active subscription will be canceled and you will not be charged again.</List.Item>
            </List.Root>
            <Text fontSize="md" fontWeight="bold" color="#18181b" lineHeight="24px">
              This action cannot be undone!
            </Text>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={closeDeleteAccountModal}>
            Cancel
          </Button>
          <Button colorPalette="red" onClick={doDeleteAccount} loading={isLoading}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteAccountModal;
